import React, {useState} from 'react';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { HashRouter, Routes, Route, Link } from 'react-router-dom';
import ChatbotSettings from './ChatbotSettings'; 
import ChatbotList from './ChatbotList'; 
import ChatbotInterface from './ChatbotInterface'; 
import { SignOutButton } from "./SignOutButton";
import ConversationList from './ConversationList';
import ImageGeneration from './ImageGeneration';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: "#D87D39"
    }
  }
});

export default function Dashboard({user, accessToken}) {
  const [open, setOpen] = React.useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleCloseProfileModel = () => {
    setProfileModalOpen(false);
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="absolute" open={open}>
          <Toolbar
            sx={{
              pr: '24px', 
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: '36px',
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              sx={{ flexGrow: 1 }}
            >
              BUas AI Assistants
            </Typography>
            { 
            <Tooltip title="View User Information" placement="bottom">
              <IconButton color="inherit" onClick={() => setProfileModalOpen(true)}>
                  <AccountCircleIcon />
              </IconButton>
            </Tooltip>
            }
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1],
            }}
          >
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav">
            <ListItemButton component={Link} to="/">
              <ListItemIcon>
                <SmartToyIcon />
              </ListItemIcon>
              <ListItemText primary="View AI Assistants" />
            </ListItemButton>    
            <ListItemButton component={Link} to="/chats">
              <ListItemIcon>
                <QuestionAnswerIcon />
              </ListItemIcon>
              <ListItemText primary="View Conversations" />
            </ListItemButton> 
            <ListItemButton component={Link} to="/image-generation">
              <ListItemIcon>
                <PhotoLibraryIcon />
              </ListItemIcon>
              <ListItemText primary="Image Generation" />
            </ListItemButton>   
            <ListItemButton component={Link} to="/new">
              <ListItemIcon>
                <AddCircleOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="Add AI Assistant" />
            </ListItemButton>                           
            <Divider sx={{ my: 1 }} />
            <SignOutButton>               
            </SignOutButton>              
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
              <Routes>            
                <Route path="/" element={
                  <>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                        <ChatbotList user={user} accessToken={accessToken} listtype={0} listtitle={"My AI Assistants"} showall={false} morelink="/my-assistants"/>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                        <ChatbotList user={user} accessToken={accessToken} listtype={2} listtitle={"BUas AI Assistants"} showall={false} morelink="/buas-assistants"/>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                        <ChatbotList user={user} accessToken={accessToken} listtype={1} listtitle={"Community AI Assistants"} showall={false} morelink="/community-assistants" createdbyinfo={true}/>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                        <ChatbotList user={user} accessToken={accessToken} listtype={3} listtitle={"AI Assistants Shared With Me"} showall={false} morelink="/shared-assistants" createdbyinfo={true}/>
                      </Paper>
                    </Grid>
                  </>
                } />
                <Route path="/my-assistants" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                      <ChatbotList user={user} accessToken={accessToken} listtype={0} listtitle={"My AI Assistants"} showall={true}/>
                    </Paper>
                  </Grid>                      
                } />
                <Route path="/buas-assistants" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                      <ChatbotList user={user} accessToken={accessToken} listtype={2} listtitle={"BUas AI Assistants"} showall={true}/>
                    </Paper>
                  </Grid>                      
                } />
                <Route path="/community-assistants" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                      <ChatbotList user={user} accessToken={accessToken} listtype={1} listtitle={"Community AI Assistants"} showall={true} createdbyinfo={true}/>
                    </Paper>
                  </Grid>                      
                } />
                <Route path="/shared-assistants" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                      <ChatbotList user={user} accessToken={accessToken} listtype={3} listtitle={"AI Assistants Shared With Me"} showall={true} createdbyinfo={true}/>
                    </Paper>
                  </Grid>                      
                } />
                <Route path="/assistant/:id" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '85vh'}}>
                      <ChatbotInterface user={user} accessToken={accessToken} initAssistant={true}/>
                    </Paper>
                  </Grid>
                } />
                <Route path="/conversation/:id" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '85vh'}}>
                      <ChatbotInterface user={user} accessToken={accessToken} initAssistant={false}/>
                    </Paper>
                  </Grid>
                } />
                <Route path="/chats" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                      <ConversationList user={user} accessToken={accessToken}/>
                    </Paper>
                  </Grid>
                } />
                <Route path="/new" element={
                  <>
                    <Grid item xs={12} md={12} lg={12}>
                      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                        <ChatbotSettings user={user} accessToken={accessToken}/> 
                      </Paper>
                    </Grid>                      
                  </>
                } />
                <Route path="/edit/:id" element={
                  <Grid item xs={12} md={12} lg={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                      <ChatbotSettings user={user} accessToken={accessToken}/>
                    </Paper>
                  </Grid>
                } />
                <Route path="/image-generation" element={
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
                      <ImageGeneration user={user} accessToken={accessToken}/>
                    </Paper>
                  </Grid>
                } />
              </Routes>                
            </Grid>            
          </Container>
        </Box>
        <Dialog open={profileModalOpen} 
                onClose={() => setProfileModalOpen(false)}
                fullWidth
                maxWidth="sm">
          <DialogTitle>User Information</DialogTitle>
          <DialogContent>
            <p><strong>Name: </strong>{user.givenName} {user.surname}</p>           
            <p><strong>Email: </strong>{user.mail}</p>
            <p><strong>User Key: </strong>{user.id}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProfileModel}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
